/*
  ProtectedRoute.tsx
  Component to protect routes that require authentication.
  Redirects to login page if user is not authenticated.
*/

import React from 'react';
import { Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

interface ProtectedRouteProps {
	children: React.ReactNode;
}

interface JwtPayload {
	exp: number; // expiration en timestamp
	[key: string]: unknown;
}

// Check if user is authenticated by verifying token existence
const isAuthenticated = (): { valid: boolean; expired: boolean } => {
	const token = localStorage.getItem('token');
	if (!token) return { valid: false, expired: false };

	try {
		const decoded: JwtPayload = jwtDecode(token);
		if (!decoded.exp) return { valid: false, expired: false };
		if (decoded.exp * 1000 > Date.now()) return { valid: true, expired: false };
		else return { valid: false, expired: true }; // Token expiré
	} catch (error) {
		console.error('Invalid token:', error);
		return { valid: false, expired: false };
	}
};

// ProtectedRoute component
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
	const auth = isAuthenticated();

	if (auth.valid) return <>{children}</>;

	// Redirection vers /login avec info d'expiration
	return (
		<Navigate
			to="/login"
			replace
			state={{
				message: auth.expired
					? 'Votre session a expiré. Veuillez vous reconnecter.'
					: undefined,
			}}
		/>
	);
};

export default ProtectedRoute;
